import { useEffect, useRef, useState } from 'react';
import { FormattedMessageDiv, LoadingAnimation, useWizardContext } from '@odmonorepo/core-components';
import { Stack } from 'react-bootstrap';
import PlaidCard from '../../Plaid/PlaidCard';
import StatementUploader from './StatementUploader';
import {getLDR, getMerchantProfile} from 'merchant-api/spiceroad';
import { decide, optimizelyInstance, useOptimizelyVariation, isOptimizelyVariationEnabled, PLAID_ACTIVE_CONNECTION_FLAG } from 'merchant-api/vendor/optimizely';
import { steps } from '../LoanAppForm';
import { loanStep } from '../helpers';
import { useAnalytics } from 'use-analytics';

type BankInfoCardProps = {
  statementUploaderDefaultOpen?: boolean;
};

export const BankInfoCard = ({ statementUploaderDefaultOpen = false }: BankInfoCardProps) => {
  const { hasOpenBankRequest, query, ldrError } = getLDR();
  const { isLoadingMerchantProfile, isErrorMerchantProfile, accountNumber, applicationType } = getMerchantProfile();
  const { goToStep } = useWizardContext<typeof steps>();
  const [variation, setVariation] = useState<string>('');
  const { applicationData } = loanStep();
  const analytics = useAnalytics();
  const { data: plaidActiveConnectionVariation, isLoading: isLoadingPlaidActiveConnection } = useOptimizelyVariation({ featureKey: PLAID_ACTIVE_CONNECTION_FLAG, analytics });
  const hasTrackedCompletionRef = useRef(false);
  useEffect(() => {
    if (optimizelyInstance) {
        // Including so this experiment can be on in staging but not impact staging tests/storybook. View the optimizely Audience to learn more about activating the experiment
      const isStagingOrDev = import.meta.env?.MODE === 'staging' || import.meta.env?.MODE === 'development';
      const firstName = applicationData?.data?.party?.persons?.[0]?.firstName;
      const optimizelyAttrs = {
        ...(isStagingOrDev && firstName
          ? { firstName: firstName }
          : {})
      };

      decide('ece_bank_page_test', optimizelyAttrs, analytics).then((result) => {
        setVariation(result || '');
      });
    }
  }, [optimizelyInstance]);

  useEffect(() => {
    const isComplete = query.status === 'success' && hasOpenBankRequest === false;
    if (!isComplete || hasTrackedCompletionRef.current) return;

    hasTrackedCompletionRef.current = true;
    analytics.track('BankInfoPageComplete', {
      experiment: 'ece_bank_page_test',
      variation: variation || 'control',
    });
    goToStep("RECEIVED");
  }, [analytics, goToStep, hasOpenBankRequest, query.status, variation]);

  if (query.status === 'pending' || isLoadingPlaidActiveConnection || isLoadingMerchantProfile )
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );

  if (ldrError || isErrorMerchantProfile) throw ldrError || isErrorMerchantProfile;
  const bankConfirmation = sessionStorage.getItem("bankConfirmation") === 'Yes';

  const bankInfoSubheading = () => {
    if (variation === 'language_only' || variation === 'pdf_accordion_and_language') {
      return (
        <FormattedMessageDiv
          id={'loanApp.step.bankInfo.subheading.experiment'}
          values={{
            b: (txt: any) => <b>{txt}</b>
          }}
        />

      );
    } else if (isOptimizelyVariationEnabled(plaidActiveConnectionVariation) && applicationType === 'RENEWAL') {
      return (
        <FormattedMessageDiv
          id={`loanApp.step.bankInfo.subheading.${bankConfirmation ? 'yes' : 'no'}`}
          values={{
            b: (txt: any) => <b>{txt}</b>,
            accountLastFour: (bankConfirmation ? accountNumber?.slice(-4) : null)
          }}
        />
      );
    }
    return null;
  }


  return (
    <Stack gap={6} style={{maxWidth: '600px', margin: 'auto'}}>
      <Stack gap={4}>
        <FormattedMessageDiv className="h3 text-center" id="renewalApp.step.final" />
        {bankInfoSubheading()}
      </Stack>
      <PlaidCard next variation={variation} />
      <StatementUploader variation={variation} defaultAccordionOpen={statementUploaderDefaultOpen} />
    </Stack>
  );
};

export default BankInfoCard;
